import { uuid } from "@/utils/uuid"
import { GLTimer, QueryTimerLogicalBlock } from "./timer-query"

export interface PerformanceCounter {
	label: string
	cpuTime: number
	gpuTime: number
}

export class PerformanceCounters {
	public isEnabled = false

	private counters: PerformanceCounter[] = []
	private timer?: GLTimer
	private blocks: Record<string, PerformanceCounter[]> = {}

	get all() {
		return this.counters
	}

	constructor(gl: WebGL2RenderingContext) {
		if (!import.meta.env.PROD) {
			this.timer = new GLTimer(gl)
			this.reset()
		}
	}

	public begin(label: string) {
		if (!this.isEnabled) {
			return
		}

		this.timer?.begin(label)
	}

	public end(label: string) {
		if (!this.isEnabled) {
			return
		}

		this.timer?.end(label, (cpuTime, gpuTime) => {
			this.add(label, cpuTime, gpuTime)
		})
	}

	public add(label: string, cpuTime: number, gpuTime: number) {
		if (!this.isEnabled) {
			return
		}

		const counter = {
			label,
			cpuTime,
			gpuTime,
		}

		this.counters.push(counter)

		for (const id in this.blocks) {
			this.blocks[id]?.push(counter)
		}
	}

	public beginLogicalBlock(label: string) {
		if (!this.isEnabled) {
			return
		}

		const id = uuid()

		this.blocks[id] = []
		this.timer?.beginLogicalBlock(label, id)
	}

	public endLogicalBlock(label: string) {
		if (!this.isEnabled) {
			return
		}

		this.timer?.endLogicalBlock(label, (block: QueryTimerLogicalBlock) => {
			const counters = this.blocks[block.id]?.filter((value) =>
				block.finishedTimers.includes(value.label),
			)

			if (!counters) {
				return
			}

			const counterToString = (
				counter: PerformanceCounter,
				totalCounter: PerformanceCounter,
			): string => {
				const relativeCPUTime = (counter.cpuTime / totalCounter.cpuTime) * 100
				const relativeGPUTime = (counter.gpuTime / totalCounter.gpuTime) * 100

				return `Perf ${counter.label}:\nPerf\t\t- CPU ${counter.cpuTime.toFixed(
					2,
				)}ms(${relativeCPUTime.toFixed(2)}%)\nPerf\t\t- GPU ${counter.gpuTime.toFixed(
					2,
				)}ms (${relativeGPUTime.toFixed(2)}%)\n`
			}

			let message = `Perf "${label}":\n`

			const totalCounter: PerformanceCounter = {
				label: "total",
				gpuTime: 0,
				cpuTime: 0,
			}

			for (const counter of counters) {
				totalCounter.cpuTime += counter.cpuTime
				totalCounter.gpuTime += counter.gpuTime
			}

			counters.push(totalCounter)

			for (const counter of counters) {
				message += counterToString(counter, totalCounter)
			}

			message += "Perf End"

			console.log(message)

			delete this.blocks[block.id]
		})
	}

	public reset() {
		this.counters = []
	}
}
